// ======================================================
// ✅ Sidebar.jsx – Navegación Lateral Institucional
// Evalúa Salud IPS
// ------------------------------------------------------
// Versión optimizada y reducida:
// • Solo muestra: Activos, Reportes, Usuarios (solo superadmin)
// • Incluye cierre de sesión y animaciones suaves.
// ======================================================

import PropTypes from "prop-types";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "../styles/Sidebar.css";

const Sidebar = memo(function Sidebar({ colapsado, abierta, onClose }) {
  const currentYear = new Date().getFullYear();
  const { usuario, cerrarSesion } = useAuth();
  const logoRef = useRef(null);
  const iconRefs = useRef([]);
  const location = useLocation();

  iconRefs.current = [];

  // ======================================================
  // 🎬 Animación de entrada (logo e íconos)
  // ======================================================
  useEffect(() => {
    if (colapsado) {return;}

    const logo = logoRef.current;
    if (logo) {
      logo.classList.remove("bounce-in");
      void logo.offsetWidth;
      logo.classList.add("bounce-in");
    }

    iconRefs.current.forEach((icon, i) => {
      if (!icon) {return;}
      icon.style.animation = "none";
      void icon.offsetWidth;
      icon.style.animation = `iconBounce 0.45s ease ${i * 0.07}s forwards`;
    });

    return () => {
      iconRefs.current.forEach((icon) => {
        if (icon) {icon.style.animation = "none";}
      });
    };
  }, [colapsado]);

  // ======================================================
  // 🔄 Cierre automático al navegar
  // ======================================================
  useEffect(() => {
    if (abierta && onClose) {onClose();}
  }, [location.pathname, abierta, onClose]);

  // ======================================================
  // ⎋ Cierre con tecla ESC
  // ======================================================
  useEffect(() => {
    if (!abierta) {return;}
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && onClose) {onClose();}
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [abierta, onClose]);

  // ======================================================
  // 🧭 Ítems del menú (reducido)
  // ======================================================
  const menuItems = useMemo(() => {
    const base = [
      { to: "/activos", label: "Activos", icon: "💼" },
      { to: "/reportes/avanzado", label: "Reportes", icon: "📊" }
    ];

    // Mostrar "Usuarios" solo si es superadmin
    if (usuario?.rol === "superadmin") {
      base.push({ to: "/usuarios", label: "Usuarios", icon: "👥" });
    }

    return base;
  }, [usuario?.rol]);

  const setIconRef = useCallback((el, index) => {
    iconRefs.current[index] = el;
  }, []);

  // ======================================================
  // 🔐 Cerrar sesión
  // ======================================================
  const handleLogout = useCallback(() => {
    cerrarSesion();
  }, [cerrarSesion]);

  // ======================================================
  // 🧱 Renderizado del sidebar
  // ======================================================
  return (
    <>
      {abierta && (
        <div
          className="sidebar__overlay"
          onClick={onClose}
          role="button"
          tabIndex={0}
          aria-label="Cerrar menú lateral"
        />
      )}

      <aside
        id="sidebar"
        className={`sidebar ${colapsado ? "collapsed" : ""} ${abierta ? "open" : ""}`}
        role="navigation"
        aria-label="Menú principal"
      >
        {/* ---------- Encabezado ---------- */}
        <header className="sidebar__header">
          <div className="sidebar__brand">
            <img
              ref={logoRef}
              src="/logo-evalua.png"
              alt="Logo Evalúa Salud IPS"
              className="sidebar__logo"
              loading="lazy"
              decoding="async"
              draggable="false"
              onError={(e) => {
                e.currentTarget.src = "/logo192.png";
              }}
            />
            {!colapsado && (
              <div className="sidebar__titlebox texto-activo">
                <h1 className="sidebar__title">Evalúa Salud IPS</h1>
                <p className="sidebar__subtitle">Gestión Institucional</p>
              </div>
            )}
          </div>
        </header>

        {/* ---------- Navegación principal ---------- */}
        <nav className="sidebar__nav" aria-label="Opciones del sistema">
          {menuItems.map(({ to, label, icon }, index) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `sidebar__link${isActive ? " active" : ""}`
              }
              title={colapsado ? label : undefined}
            >
              <span
                className="sidebar__icon"
                ref={(el) => setIconRef(el, index)}
                aria-hidden="true"
              >
                {icon}
              </span>
              {!colapsado && (
                <span className="sidebar__text texto-activo">{label}</span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* ---------- Cierre de sesión ---------- */}
        <div className="sidebar__logoutWrap">
          <button
            type="button"
            className="sidebar__logout"
            onClick={handleLogout}
            title="Cerrar sesión"
            aria-label="Cerrar sesión"
          >
            <svg
              className="sidebar__logoutIcon"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M15 3H8a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h7"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <path
                d="M10 12h9m0 0-3-3m3 3-3 3"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {!colapsado && <span> Cerrar sesión</span>}
          </button>

          {!colapsado && (
            <footer className="sidebar__footer texto-activo">
              <small>© {currentYear} Evalúa Salud IPS</small>
            </footer>
          )}
        </div>
      </aside>
    </>
  );
});

Sidebar.displayName = "Sidebar";

Sidebar.propTypes = {
  colapsado: PropTypes.bool.isRequired,
  abierta: PropTypes.bool,
  onClose: PropTypes.func
};

export default Sidebar;
